// Material 3 Navigation Bar: bottom destinations for compact screens (shown below the `md` breakpoint).
// Props: items=[{ id, label, icon, inMenu?, children? }], value (active id), onChange(id, element, { fromMenu }),
// menuLabel ('Menu'). Items with `inMenu` are not shown as destinations: they are grouped behind one hamburger destination
// (placed where the first of them was) that opens a menu above the bar, so the bar stays short however many sections there are.
// An item's `children` are listed after it in that menu. The hamburger is active while the current page is one of them.
(function () {
  const { Box } = MaterialUI;
  const { useState } = React;

  function NavigationBar({ items = [], value, onChange, menuLabel = 'Menu' }) {
    const { GoogleIcon } = window.Components;
    const [menuAnchor, setMenuAnchor] = useState(null);
    const MENU_ID = '__menu';
    const inMenu = items.filter(i => i.inMenu);
    const entries = inMenu.flatMap(i => [{ ...i, children: undefined }, ...(i.children || [])]);
    const menuActive = entries.some(e => e.id === value);
    const first = items.findIndex(i => i.inMenu);
    const bar = !inMenu.length ? items : [
      ...items.slice(0, first), { id: MENU_ID, label: menuLabel, icon: 'menu', isMenu: true }, ...items.slice(first)
    ].filter(i => !i.inMenu);
    return (
      <>
      <Box
        component="nav"
        aria-label="Primary"
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1100,
          height: 80,
          pb: 'env(safe-area-inset-bottom, 0px)',
          boxSizing: 'content-box',
          backgroundColor: 'color-mix(in srgb, var(--m3-surface-container) 88%, transparent)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderTop: '1px solid var(--m3-outline-variant)'
        }}
      >
        {bar.map(item => {
          const active = item.isMenu ? menuActive : item.id === value;
          return (
            <Box
              key={item.id}
              component="button"
              type="button"
              onClick={e => (item.isMenu ? setMenuAnchor(e.currentTarget) : onChange && onChange(item.id, e.currentTarget))}
              aria-haspopup={item.isMenu ? 'menu' : undefined}
              aria-expanded={item.isMenu ? !!menuAnchor : undefined}
              aria-current={active ? 'page' : undefined}
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                background: 'none',
                border: 0,
                p: 0,
                cursor: 'pointer',
                color: active ? 'var(--m3-on-surface)' : 'var(--m3-on-surface-variant)',
                '&:hover .m3-nav-indicator': {
                  backgroundColor: active
                    ? 'var(--m3-secondary-container)'
                    : 'color-mix(in srgb, var(--m3-on-surface) 8%, transparent)'
                }
              }}
            >
              <Box
                className="m3-nav-indicator"
                sx={{
                  width: 64,
                  height: 32,
                  borderRadius: 'var(--m3-shape-full)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: active ? 'var(--m3-on-secondary-container)' : 'inherit',
                  backgroundColor: active ? 'var(--m3-secondary-container)' : 'transparent',
                  transition: 'background-color var(--m3-duration-short) var(--m3-easing-standard)'
                }}
              >
                <GoogleIcon name={item.icon} size={24} filled={active} />
              </Box>
              <Box component="span" sx={{ font: 'var(--m3-type-label-medium)', fontFamily: "var(--wb-font-nav, 'Google Sans', sans-serif)", fontSynthesis: 'none', fontWeight: active ? 700 : 500, whiteSpace: 'nowrap' }}>
                {item.label}
              </Box>
            </Box>
          );
        })}
      </Box>
      <window.Components.NavMenu
        items={inMenu}
        value={value}
        onSelect={id => onChange && onChange(id, menuAnchor, { fromMenu: true })}
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        minWidth={240}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        PaperProps={{ sx: { mt: -1, maxHeight: 'min(70dvh, 420px)' } }}
      />
      </>
    );
  }

  window.Components = window.Components || {};
  window.Components.NavigationBar = NavigationBar;
})();
